import React, { useState } from 'react';
import { PlayIcon, PauseIcon, SettingsIcon, FileTextIcon, TrashIcon, CheckCircleIcon, AlertTriangleIcon, XCircleIcon } from 'lucide-react';
export function IntegrationsConnectionsTab() {
  const connections = [{
    id: 1,
    name: 'Salesforce Production',
    environment: 'Production',
    status: 'healthy',
    dataDomains: ['Accounts', 'Contacts', 'Opportunities'],
    lastSync: '2 min ago',
    nextSync: 'In 58 min',
    owner: 'Sarah Chen',
    errors: 0
  }, {
    id: 2,
    name: 'HubSpot',
    environment: 'Production',
    status: 'healthy',
    dataDomains: ['Contacts', 'Companies', 'Deals'],
    lastSync: '5 min ago',
    nextSync: 'In 55 min',
    owner: 'Mike Rodriguez',
    errors: 0
  }, {
    id: 3,
    name: 'Marketo',
    environment: 'Production',
    status: 'warning',
    dataDomains: ['Leads', 'Programs'],
    lastSync: '45 min ago',
    nextSync: 'In 15 min',
    owner: 'Sarah Chen',
    errors: 3
  }, {
    id: 4,
    name: 'Salesforce Sandbox',
    environment: 'Sandbox',
    status: 'paused',
    dataDomains: ['Accounts', 'Contacts'],
    lastSync: '2 hours ago',
    nextSync: 'Paused',
    owner: 'Mike Rodriguez',
    errors: 0
  }, {
    id: 5,
    name: 'Google Ads',
    environment: 'Production',
    status: 'healthy',
    dataDomains: ['Campaigns', 'Audiences'],
    lastSync: '1 min ago',
    nextSync: 'In 59 min',
    owner: 'Lisa Park',
    errors: 0
  }];
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircleIcon className="w-5 h-5 text-green-600" data-id="element-1598" />;
      case 'warning':
        return <AlertTriangleIcon className="w-5 h-5 text-yellow-600" data-id="element-1599" />;
      case 'error':
        return <XCircleIcon className="w-5 h-5 text-red-600" data-id="element-1600" />;
      case 'paused':
        return <PauseIcon className="w-5 h-5 text-gray-600" data-id="element-1601" />;
      default:
        return <CheckCircleIcon className="w-5 h-5 text-gray-600" data-id="element-1602" />;
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'bg-green-100 text-green-700';
      case 'warning':
        return 'bg-yellow-100 text-yellow-700';
      case 'error':
        return 'bg-red-100 text-red-700';
      case 'paused':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  return <div className="space-y-6" data-id="element-1603">
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4" data-id="element-1604">
        <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1605">
          <p className="text-sm text-gray-600 mb-1" data-id="element-1606">Total Connections</p>
          <p className="text-3xl font-bold text-gray-900" data-id="element-1607">5</p>
          <p className="text-xs text-gray-600 mt-1" data-id="element-1608">2/6 limit (Growth)</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1609">
          <p className="text-sm text-gray-600 mb-1" data-id="element-1610">Healthy</p>
          <p className="text-3xl font-bold text-green-600" data-id="element-1611">3</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1612">
          <p className="text-sm text-gray-600 mb-1" data-id="element-1613">Warnings</p>
          <p className="text-3xl font-bold text-yellow-600" data-id="element-1614">1</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1615">
          <p className="text-sm text-gray-600 mb-1" data-id="element-1616">Paused</p>
          <p className="text-3xl font-bold text-gray-600" data-id="element-1617">1</p>
        </div>
      </div>
      {/* Bulk Actions */}
      <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1618">
        <div className="flex items-center justify-between" data-id="element-1619">
          <p className="text-sm font-medium text-gray-900" data-id="element-1620">Bulk Actions</p>
          <div className="flex items-center gap-2" data-id="element-1621">
            <button className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm" data-id="element-1622">
              Pause All
            </button>
            <button className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm" data-id="element-1623">
              Resume All
            </button>
          </div>
        </div>
      </div>
      {/* Connections Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-1624">
        <table className="w-full" data-id="element-1625">
          <thead className="bg-gray-50 border-b border-gray-200" data-id="element-1626">
            <tr data-id="element-1627">
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-1628">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-1629">
                Environment
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-1630">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-1631">
                Data Domains
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-1632">
                Last Sync
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-1633">
                Next Sync
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-1634">
                Owner
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-1635">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200" data-id="element-1636">
            {connections.map(connection => <tr key={connection.id} className="hover:bg-gray-50" data-id="element-1637">
                <td className="px-6 py-4" data-id="element-1638">
                  <div className="flex items-center gap-2" data-id="element-1639">
                    {getStatusIcon(connection.status)}
                    <span className="text-sm font-medium text-gray-900" data-id="element-1640">
                      {connection.name}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4" data-id="element-1641">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${connection.environment === 'Production' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-1642">
                    {connection.environment}
                  </span>
                </td>
                <td className="px-6 py-4" data-id="element-1643">
                  <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${getStatusColor(connection.status)}`} data-id="element-1644">
                    {connection.status}
                  </span>
                </td>
                <td className="px-6 py-4" data-id="element-1645">
                  <div className="flex flex-wrap gap-1" data-id="element-1646">
                    {connection.dataDomains.map(domain => <span key={domain} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs" data-id="element-1647">
                        {domain}
                      </span>)}
                  </div>
                </td>
                <td className="px-6 py-4" data-id="element-1648">
                  <span className="text-sm text-gray-900" data-id="element-1649">
                    {connection.lastSync}
                  </span>
                </td>
                <td className="px-6 py-4" data-id="element-1650">
                  <span className="text-sm text-gray-900" data-id="element-1651">
                    {connection.nextSync}
                  </span>
                </td>
                <td className="px-6 py-4" data-id="element-1652">
                  <span className="text-sm text-gray-900" data-id="element-1653">
                    {connection.owner}
                  </span>
                </td>
                <td className="px-6 py-4" data-id="element-1654">
                  <div className="flex items-center gap-2" data-id="element-1655">
                    {connection.status === 'paused' ? <button className="p-1.5 hover:bg-gray-100 rounded transition-colors" title="Resume" data-id="element-1656">
                        <PlayIcon className="w-4 h-4 text-gray-600" data-id="element-1657" />
                      </button> : <button className="p-1.5 hover:bg-gray-100 rounded transition-colors" title="Pause" data-id="element-1658">
                        <PauseIcon className="w-4 h-4 text-gray-600" data-id="element-1659" />
                      </button>}
                    <button className="p-1.5 hover:bg-gray-100 rounded transition-colors" title="Configure" data-id="element-1660">
                      <SettingsIcon className="w-4 h-4 text-gray-600" data-id="element-1661" />
                    </button>
                    <button className="p-1.5 hover:bg-gray-100 rounded transition-colors" title="View Logs" data-id="element-1662">
                      <FileTextIcon className="w-4 h-4 text-gray-600" data-id="element-1663" />
                    </button>
                    <button className="p-1.5 hover:bg-gray-100 rounded transition-colors" title="Remove" data-id="element-1664">
                      <TrashIcon className="w-4 h-4 text-red-600" data-id="element-1665" />
                    </button>
                  </div>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}